/* eslint-disable */
import { connect } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formActions, modalActions } from '../../../Actions';
import { BaseModal } from '../BaseModal';
import { notificationServices } from '../../../Services';
import regionStateServices from '../../../Services/regionState.services';
import assetAuditServices from '../../../Services/assetAudit-services';
import StepWiseActionComponent from '../../StepWiseActionComponent/StepWiseActionComponent';
import AuditStep1 from '../AuditCreationModal/AuditStep1/AuditStep1';
import AuditStep2 from '../AuditCreationModal/AuditStep2/AuditStep2';
import AssetAuditStep3 from './AssetAuditStep3/AssetAuditStep3';
import AssetAuditStep4 from './AssetAuditStep4/AssetAuditStep4';

/**
 * Asset audit creation wizard — forked from ProcessAuditCreationModal, which is
 * the precedent for adding an audit flavour to this page.
 *
 * Name -> Store(s) -> Asset Types -> Verify. Store selection reuses the store
 * audit's own AuditStep2 region/state/city/store cascader verbatim, fed by
 * regionStateServices.getAllRegionMappings — the same source the store audit
 * uses, so regions look and behave identically across the three tabs.
 *
 * Confirm creates ONE audit per selected store in a single backend call; the
 * backend refuses the whole request if any store fails a guard, so there is no
 * partial-success state to render here.
 */

// Column headers AND per-level colours for the store cascader. Copied
// verbatim from AuditCreationModal's `regions` const — AuditStep2 passes this
// straight to MultiCascaderTree as `headers`, so its LENGTH decides how many
// columns the tree renders. A short array silently drops the STORE column and
// leaves the picker unusable.
const regions = [
	{
		label: 'All Region',
		color: '#94a3b8',
	},
	{
		label: 'Region',
		color: '#facc15',
	},
	{
		label: 'State',
		color: '#008080',
	},
	{
		label: 'City',
		color: '#FF7F50',
	},
	{
		label: 'Store',
		color: '#2dd4bf',
	},
];

const AssetAuditCreationModal = (props) => {
	const {
		show,
		data,
		toggleModal,
		handleConfirm,
		stores,
		permittedStores,
		fetchStoresList,
		toggleListModal,
	} = props;
	const { heading = 'Start New Asset Audit' } = data || {};

	const initialState = {
		auditName: '',
		selectedStores: [],
		// The picked tree nodes themselves, kept so the verify step can name
		// the stores without waiting on the redux STORES list — which is
		// dispatched in the same effect that opens this modal and may not have
		// landed yet, making the two counts on step 4 disagree for a beat.
		selectedStoreNodes: [],
		selectedStoreTypes: [],
		assetTypeIds: [],
	};

	const [state, setState] = useState(initialState);
	const [inputState, setInputState] = useState([]);
	const [rawInputState, setRawInputState] = useState([]);
	const [validStoreTypes, setValidStoreTypes] = useState({});
	const [validStoreIds, setValidStoreIds] = useState({});
	const [assetTypes, setAssetTypes] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	// Kept apart from isLoading: a failed fetch and an empty tenant both end up
	// with no asset types, and step 3 must say which one happened.
	const [assetTypesFailed, setAssetTypesFailed] = useState(false);

	const setStateField = (field) => (value) => {
		setState((prev) => ({ ...prev, [field]: value }));
	};

	const handleClose = () => {
		toggleModal(false);
		setState(initialState);
		setInputState([]);
	};

	const fetchInputState = async () => {
		const response = await regionStateServices?.getAllRegionMappings();
		setRawInputState(response);
		const allRegion = convertJSON(response);
		allRegion[0].select = -1;
		setInputState(allRegion);
	};

	useEffect(() => {
		if (show) {
			setState(initialState);
			fetchInputState();
			fetchStoresList({
				entity: 'STORES',
				req: {
					params: {
						id: '',
					},
				},
				method: 'LIST',
			});
			if (!permittedStores || permittedStores.length === 0) {
				setState((prev) => ({
					...prev,
					selectedStoreTypes: [
						'BASE',
						'RETAIL',
						'WAREHOUSE_INGREDIENT',
						'WAREHOUSE',
						'WAREHOUSE_PRODUCT',
					],
				}));
			} else {
				const jsonValidStoreType = {};
				const jsonValidStoreIds = {};
				permittedStores?.forEach((store) => {
					jsonValidStoreType[store.storeType] = 1;
					jsonValidStoreIds[store.id] = 1;
				});
				setValidStoreTypes(jsonValidStoreType);
				setValidStoreIds(jsonValidStoreIds);
				setState((prev) => ({
					...prev,
					selectedStoreTypes: Object.keys(jsonValidStoreType),
				}));
			}
			fetchAssetTypes();
		}
	}, [show]);

	const fetchAssetTypes = async () => {
		try {
			setIsLoading(true);
			setAssetTypesFailed(false);
			const response = await assetAuditServices.fetchAuditableAssetTypes({ data: {} });
			if (response?.statusCode === 200) {
				setAssetTypes(response?.data?.assetTypes ?? []);
			} else {
				setAssetTypesFailed(true);
			}
		} catch (error) {
			setAssetTypesFailed(true);
			// handleResponse already toasted whatever the backend said.
			// eslint-disable-next-line no-console
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	function convertJSON(jsonArray, level = 0) {
		return jsonArray.map((item) => {
			const newItem = {
				...item,
				color: level < regions.length ? regions[level].color : 'gray',
			};
			if (item?.children) {
				if (item?.type === 'city') {
					newItem.children = item?.children?.filter((ele) => {
						return state?.selectedStoreTypes?.includes(
							ele?.storeType
						) && (permittedStores.length === 0 || validStoreIds[ele?.id]);
					}).map((ele) => {
						return {
							...ele,
							color: level < regions.length - 1 ? regions[level + 1].color : 'gray',
						};
					});
				} else {
					newItem.children = convertJSON(item.children, level + 1);
				}
			}
			return newItem;
		});
	}

	useEffect(async () => {
		if (show) {
			const allRegion = convertJSON(rawInputState);
			if (allRegion?.[0]) {
				allRegion[0].select = -1;
			}
			setInputState(allRegion);
		}
	}, [show, state?.selectedStoreTypes?.length, rawInputState]);

	const RegionDependingStoresFetch = (itemToCheck, parentStatus) => {
		// eslint-disable-next-line prefer-const
		let allStores = [];
		itemToCheck?.forEach((ele) => {
			if (
				ele.type === 'store' &&
				(ele?.select === 1 || (ele.select === 0 && parentStatus === 1))
			) {
				allStores.push(ele);
			}
			// eslint-disable-next-line max-len
			const childStores = RegionDependingStoresFetch(
				ele.children,
				ele.select === 0 ? parentStatus : ele.select
			);
			allStores = [...allStores, ...childStores];
		});
		return allStores;
	};

	const getAllStores = useCallback(() => {
		return RegionDependingStoresFetch(inputState, 0);
	}, [inputState]);

	const handleStoreTypeSelection = (value) => {
		setState((prev) => {
			return {
				...prev,
				selectedStoreTypes: value,
			};
		});
	};

	const selectedStoreObjects = useMemo(() => {
		const storeById = new Map((stores ?? []).map((store) => [store?.id, store]));
		return (state.selectedStoreNodes ?? []).map((node) => ({
			id: node?.id,
			name: node?.title ?? node?.name ?? storeById.get(node?.id)?.name ?? '-',
		}));
	}, [stores, state.selectedStoreNodes]);

	const selectedAssetTypes = useMemo(
		() => assetTypes.filter((type) => state.assetTypeIds.includes(type.id)),
		[assetTypes, state.assetTypeIds]
	);

	const steps = [
		{
			stepHeader: 'Audit Name',
			showBack: true,
			backBtnTxt: 'Cancel',
			showNext: true,
			onBack: (_, cb) => {
				handleClose();
				cb();
			},
			onNext: (_, cb) => {
				if (!state.auditName) {
					notificationServices.generateNotification({
						type: 'error',
						message: 'Please enter Audit name',
					});
					return;
				}
				cb();
			},
			renderComponent: (currentStep) => (
				<AuditStep1
					auditName={state.auditName}
					setAuditName={setStateField('auditName')}
					scheduledFor={null}
					setScheduledFor={() => {}}
					currentStep={currentStep}
					show={show}
					hideSchedule
				/>
			),
		},
		{
			stepHeader: 'Audit Store(s)',
			showBack: true,
			showNext: true,
			showTooltip: true,
			tooltipContent: (
				<div>
					<p>
						Total Stores Selected:
						{' '}
						{state?.selectedStores?.length}
					</p>
				</div>
			),
			onBack: (_, cb) => cb(),
			onNext: (_, cb) => {
				const allSelectedStores = getAllStores();
				if (!allSelectedStores?.length) {
					notificationServices.generateNotification({
						type: 'error',
						message: 'Please select Store(s)',
					});
					return;
				}
				setState((prev) => ({
					...prev,
					selectedStores: allSelectedStores.map((store) => store?.id),
					selectedStoreNodes: allSelectedStores,
				}));
				cb();
			},
			renderComponent: () => (
				<AuditStep2
					toggleListModal={toggleListModal}
					deleteFilter={() => {}}
					selectedStores={state?.selectedStores}
					stores={stores}
					inputState={inputState}
					setInputState={setInputState}
					regions={regions}
					setAuditCreationState={setState}
					auditCreationState={state}
					handleStoreTypeSelection={handleStoreTypeSelection}
					validStoreTypes={validStoreTypes}
				/>
			),
		},
		{
			stepHeader: 'Asset Types',
			showBack: true,
			showNext: true,
			onBack: (_, cb) => cb(),
			onNext: (_, cb) => {
				if (!state.assetTypeIds.length) {
					notificationServices.generateNotification({
						type: 'error',
						message: 'Please select at least one asset type',
					});
					return;
				}
				cb();
			},
			renderComponent: () => (
				<AssetAuditStep3
					assetTypes={assetTypes}
					isLoading={isLoading}
					hasError={assetTypesFailed}
					retryFetch={fetchAssetTypes}
					selectedAssetTypeIds={state.assetTypeIds}
					setSelectedAssetTypeIds={setStateField('assetTypeIds')}
				/>
			),
		},
		{
			stepHeader: 'Verify',
			showBack: true,
			showNext: true,
			nextBtnTxt: 'Confirm',
			onBack: (_, cb) => cb(),
			onNext: () => {
				handleConfirm({
					name: state.auditName,
					storeIds: state.selectedStores,
					assetTypeIds: state.assetTypeIds,
				});
			},
			renderComponent: () => (
				<AssetAuditStep4
					auditName={state.auditName}
					selectedStores={selectedStoreObjects}
					selectedAssetTypes={selectedAssetTypes}
				/>
			),
		},
	];

	return (
		<BaseModal
			customStyles={{ width: '75%', height: '45rem' }}
			modalCallee='assetAuditCreator'
			heading={heading}
			open={show}
			handleClose={handleClose}
			hideFooter
		>
			<StepWiseActionComponent steps={steps} />
		</BaseModal>
	);
};

const mapStateToProps = (state) => {
	const modalSlice = state?.modal?.assetAuditCreationModal || { show: false, data: {} };
	const { show, data } = modalSlice;
	const stores = state?.form?.STORES?.data?.LIST;
	const { permittedStores } = state?.navigation;
	return {
		show,
		data,
		stores,
		permittedStores,
	};
};

const mapDispatchToProps = {
	toggleModal: modalActions.toggleAssetAuditCreationModal,
	toggleListModal: modalActions.toggleListModal,
	fetchStoresList: formActions.gateway,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetAuditCreationModal);
